import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import React, { memo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import DropdownButton from "react-bootstrap/DropdownButton";
import Select from "react-select";
import { toast } from "react-toastify";
import { useQuery } from "react-query";
import PermissionComponent from "Shared/Common/Permissions/PermissionComponent";
import { CrossIcon, DoneIcon, LoaderIcon } from "Shared/Icons";
import useLang from "Shared/hooks/useLanguage";
import { styles } from "Utils/Common";
import { PanelsAgainstLabId } from "Services/OncologyCompendiumServices/PlatingRuleService";

type PanelSelection = {
  panelId: number;
  panelName: string;
};

type SelectOption = {
  value: number;
  label: string;
};

export interface ReflexRuleRow {
  id: number;
  labId: number;
  panelId: string;
  labName: string;
  panelName: string;
  panels?: PanelSelection[];
  rowStatus: boolean;
  ordersSelectedOnPanelForNY: number;
  ordersSelectedOnPanel: number;
}

const getPanelsFromRow = (row: ReflexRuleRow): PanelSelection[] => {
  if (Array.isArray(row.panels)) {
    return row.panels;
  }

  try {
    const parsedPanels = JSON.parse(row.panelId || "[]");

    return Array.isArray(parsedPanels)
      ? parsedPanels
          .map((panel: any) => ({
            panelId: Number(panel?.panelId ?? panel?.PanelId),
            panelName: panel?.panelName ?? panel?.PanelName ?? "",
          }))
          .filter((panel: PanelSelection) => panel.panelId > 0)
      : [];
  } catch {
    return [];
  }
};

const serializePanels = (panels: PanelSelection[]) => JSON.stringify(panels);

const Row = (props: {
  rows: any;
  setRows: any;
  index: number;
  row: ReflexRuleRow;
  queryDisplayTagNames: any;
  setIsAddButtonDisabled: any;
  performingLabOptions: any[];
  onSave: (payload: any) => Promise<any>;
}) => {
  const {
    row,
    rows,
    index,
    setRows,
    queryDisplayTagNames,
    setIsAddButtonDisabled,
    performingLabOptions,
    onSave,
  } = props;

  const { t } = useLang();

  const [loader, setLoader] = useState(false);
  const selectedPanels = getPanelsFromRow(row);

  const { data: panelOptions } = useQuery(
    ["oncology-plating-rule", "panels", row?.labId],
    async () => {
      const res = await PanelsAgainstLabId(row?.labId ?? null);
      const raw = (res as any)?.data?.data ?? (res as any)?.data ?? [];
      const arr = Array.isArray(raw)
        ? raw
        : Array.isArray((raw as any)?.data)
          ? (raw as any).data
          : [];
      return arr.map(
        (x: any): SelectOption => ({
          value: Number(x?.value ?? x?.panelId ?? x?.id),
          label: x?.label ?? x?.panelDisplayName ?? x?.panelName ?? x?.name,
        })
      );
    },
    {
      enabled: Boolean(row?.rowStatus) && Number(row?.labId) > 0,
      staleTime: 10 * 60 * 1000,
    }
  );

  const getValues = (r: any, action: string) => {
    if (action === "Edit") {
      const updatedRows = rows.map((row: any) => {
        if (row.id === r.id) {
          return {
            ...row,
            rowStatus: true,
          };
        }
        return row;
      });

      setRows(updatedRows);
    }
  };

  const handleSubmit = async () => {
    const requiredFields = {
      labId: row.labId,
      panelId: selectedPanels.length,
      ordersSelectedOnPanelForNY: row.ordersSelectedOnPanelForNY,
      ordersSelectedOnPanel: row.ordersSelectedOnPanel,
    };

    const invalidField =
      Object.entries(requiredFields).find(([, value]) => !value)?.[0] ?? null;

    if (!invalidField) {
      const panelName = selectedPanels
        .map((panel) => panel.panelName)
        .join(", ");

      const submittedResults = {
        id: row.id,
        labId: row.labId,
        labName:
          row.labName ??
          performingLabOptions?.find((p: any) => p.value === row.labId)?.label,
        panelId: serializePanels(selectedPanels),
        panelName,
        ordersSelectedOnPanelForNY: Number(row.ordersSelectedOnPanelForNY) || 0,
        ordersSelectedOnPanel: Number(row.ordersSelectedOnPanel) || 0,
      };

      setLoader(true);

      try {
        await onSave(submittedResults);
      } catch (error) {
        toast.error(t("Something went wrong while saving data."));
        console.error("Error saving reflex rule:", error);
      } finally {
        setLoader(false);
      }
    } else {
      const errorMessage = invalidField
        ? `Please Fill ${queryDisplayTagNames[invalidField] ?? ""} field`
        : "Invalid field found";
      toast.error(errorMessage);
    }
  };

  const handleChange = (name: string, value: any, id: number) => {
    if (name === "labId") {
      setRows((curr: any) =>
        curr.map((x: any) => {
          if (x.id !== id) return x;
          return {
            ...x,
            panelId: "[]",
            panelName: "",
            panels: [],
          };
        })
      );
    }

    if (name === "panels") {
      const panels = (Array.isArray(value) ? value : []).map(
        (option: SelectOption) => ({
          panelId: Number(option.value),
          panelName: option.label,
        })
      );

      setRows((curr: any) =>
        curr.map((x: any) => {
          if (x.id !== id) return x;
          return {
            ...x,
            panelId: serializePanels(panels),
            panelName: panels.map((panel) => panel.panelName).join(", "),
            panels,
          };
        })
      );
      return;
    }

    setRows((curr: any) =>
      curr.map((x: any) => {
        if (x.id !== id) return x;
        return {
          ...x,
          [name]: value,
        };
      })
    );
  };

  return (
    <React.Fragment>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <div className="d-flex justify-content-center">
            {row.rowStatus ? (
              <>
                <div className="gap-2 d-flex">
                  {loader ? (
                    <button
                      id={`compendiumDataPanelMapingLoadButton`}
                      className="btn btn-icon btn-sm fw-bold btn-table-save btn-icon-light h-32px w-32px fas-icon-20px"
                    >
                      <LoaderIcon />
                    </button>
                  ) : (
                    <button
                      id={`IDCompendiumDataReflexRuleSaveRow`}
                      onClick={handleSubmit}
                      className="btn btn-icon btn-sm fw-bold btn-table-save btn-icon-light h-32px w-32px fas-icon-20px"
                    >
                      <DoneIcon />
                    </button>
                  )}
                  <button
                    id={`IDCompendiumDataReflexRuleCancelSaveRow`}
                    onClick={() => {
                      if (row.id !== 0) {
                        const updatedRows = rows.map((r: any) => {
                          if (r.id === row.id) {
                            return { ...r, rowStatus: false };
                          }
                          return r;
                        });
                        setRows(updatedRows);
                      } else {
                        const newArray = [...rows];
                        newArray.splice(index, 1);
                        setRows(newArray);
                        setIsAddButtonDisabled(false);
                      }
                    }}
                    className="btn btn-icon btn-sm fw-bold btn-table-cancel btn-icon-light h-32px w-32px fas-icon-20px"
                  >
                    <CrossIcon />
                  </button>
                </div>
              </>
            ) : (
              <div className="rotatebtnn">
                <DropdownButton
                  className="p-0 del-before btn btn-light-info btn-active-info btn-sm btn-action table-action-btn"
                  key="end"
                  id={`OncologyCompendiumDataReflexRule3Dots_${row.id}`}
                  drop="end"
                  title={<i className="bi bi-three-dots-vertical p-0"></i>}
                >
                  <PermissionComponent
                    moduleName="ID LIS"
                    pageName="Compendium Data"
                    permissionIdentifier="Edit"
                  >
                    <Dropdown.Item
                      id={`OncologyCompendiumDataReflexRuleEdit`}
                      eventKey="2"
                      onClick={() => getValues(row, "Edit")}
                      className="w-auto"
                    >
                      <span className="menu-item px-3">
                        <i
                          className="fa fa-edit"
                          style={{ fontSize: "16px", color: "green" }}
                        ></i>
                        {t("Edit")}
                      </span>
                    </Dropdown.Item>
                  </PermissionComponent>
                </DropdownButton>
              </div>
            )}
          </div>
        </TableCell>
        <TableCell
          id={`IDCompendiumDataReflexRulePerformingLab_${row.id}`}
          component="th"
          scope="row"
        >
          {row.rowStatus ? (
            <div className="required d-flex">
              <div className="w-100">
                <Select
                  inputId="OncologyCompendiumDataReflexRule_performingLab"
                  menuPortalTarget={document.body}
                  options={performingLabOptions ?? []}
                  theme={(theme) => styles(theme)}
                  placeholder={t("Lab Name")}
                  name="labId"
                  value={(performingLabOptions ?? [])?.filter(
                    (item: any) => item?.value === row?.labId
                  )}
                  onChange={(event: any) => {
                    handleChange("labId", event.value, row.id);
                  }}
                />
              </div>
            </div>
          ) : (
            <span>{row?.labName}</span>
          )}
        </TableCell>
        <TableCell
          id={`IDCompendiumDataReflexRuleFacility_${row.id}`}
          component="th"
          scope="row"
        >
          {row.rowStatus ? (
            <div className="required d-flex">
              <div className="w-100">
                <Select
                  inputId="IDCompendiumDataReflexRule_panelName"
                  menuPortalTarget={document.body}
                  options={panelOptions ?? []}
                  theme={(theme) => styles(theme)}
                  placeholder={t(" Panel ")}
                  name="panelId"
                  isMulti
                  closeMenuOnSelect={false}
                  value={(panelOptions ?? [])?.filter((item: SelectOption) =>
                    selectedPanels.some((panel) => panel.panelId === item.value)
                  )}
                  onChange={(event: any) => {
                    handleChange("panels", event, row.id);
                  }}
                />
              </div>
            </div>
          ) : (
            <span className="text-capitalize">
              {row?.panelName ||
                selectedPanels.map((panel) => panel.panelName).join(", ")}
            </span>
          )}
        </TableCell>
        <TableCell
          id={`IDCompendiumDataOrdersSelectedOnPanelForNY_${row.id}`}
          component="th"
          scope="row"
        >
          {row.rowStatus ? (
            <div className="required d-flex">
              <div className="w-100">
                <input
                  type="number"
                  autoComplete="off"
                  name="ordersSelectedOnPanelForNY"
                  className="form-control bg-white min-w-100px w-100 rounded-2 fs-8 h-30px"
                  placeholder={t(
                    "Number of Order selected On Panel for NY State"
                  )}
                  value={row?.ordersSelectedOnPanelForNY ?? ""}
                  onChange={(event: any) =>
                    handleChange(event.target.name, event.target.value, row?.id)
                  }
                />
              </div>
            </div>
          ) : (
            <span>{row?.ordersSelectedOnPanelForNY}</span>
          )}
        </TableCell>
        <TableCell
          id={`IDCompendiumDataOrdersSelectedOnPanel_${row.id}`}
          component="th"
          scope="row"
        >
          {row.rowStatus ? (
            <div className="required d-flex">
              <div className="w-100">
                <input
                  type="number"
                  autoComplete="off"
                  name="ordersSelectedOnPanel"
                  className="form-control bg-white min-w-100px w-100 rounded-2 fs-8 h-30px"
                  placeholder={t("Number of Order selected On Panel")}
                  value={row?.ordersSelectedOnPanel ?? ""}
                  onChange={(event: any) =>
                    handleChange(event.target.name, event.target.value, row?.id)
                  }
                />
              </div>
            </div>
          ) : (
            <span>{row?.ordersSelectedOnPanel}</span>
          )}
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export default memo(Row);
